import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import chevronGrayIcon from 'assets/chevron-gray.svg';
import plusOutlineGrayIcon from 'assets/plus-outline-gray.svg';

import styles from 'Pages/TeamDashboard/ProductLibrary/styles.module.scss';

const CategoryNav = ({
  categories,
  selectedFlowUuid,
  expandedCategories,
  onFlowSelect,
  onCategoryToggle,
  onAddFlow,
  selectionMode,
  selectedLibraryFlowUuid,
  screenCount,
}) => {
  const navScrollRef = useRef(null);
  const activeItemRef = useRef(null);

  useEffect(() => {
    const scrollEl = navScrollRef.current;
    const itemEl = activeItemRef.current;

    if (!itemEl || !scrollEl) {
      return;
    }

    const itemTop = itemEl.offsetTop;
    const itemBottom = itemTop + itemEl.offsetHeight;

    if (itemTop < scrollEl.scrollTop) {
      scrollEl.scrollTop = itemTop - 8;
    }
    else if (itemBottom > scrollEl.scrollTop + scrollEl.clientHeight) {
      scrollEl.scrollTop = itemBottom - scrollEl.clientHeight + 8;
    }
  }, [selectedFlowUuid]);

  return (
    <nav className={styles.navPanel}>
      <h2 className={styles.navTitle}>
        {selectionMode ? 'Select your screens' : 'Product library'}
      </h2>

      {selectionMode && (
        <span className={styles.navScreenCountBadge}>
          {screenCount ?? 0}
          {' '}
          out of
          {' '}
          20
        </span>
      )}

      <div className={styles.navTreeContainer}>
        <div ref={navScrollRef} className={styles.navTreeScroll}>
          <div className={styles.navTreeContent}>
            {categories.map(category => (
              <div key={category.uuid} className={styles.navCategoryGroup}>
                <button
                  className={styles.navCategoryHeader}
                  onClick={() => onCategoryToggle(category.uuid)}
                >
                  <span className={styles.navCategoryName}>
                    {category.name}
                  </span>
                  <img
                    src={chevronGrayIcon}
                    alt=""
                    className={`${styles.navChevron}
                    ${expandedCategories.has(category.uuid) ? styles.chevronFlipped : ''}`}
                  />
                </button>

                {expandedCategories.has(category.uuid) && (
                  <div className={styles.navFlowList}>
                    {category.flows.map(flow => {
                      const isScrollActive = selectedFlowUuid === flow.uuid;
                      const isLibrarySelected = selectedLibraryFlowUuid === flow.uuid;
                      const isActiveItem = isScrollActive;

                      return (
                        <button
                          key={flow.uuid}
                          ref={isActiveItem ? activeItemRef : null}
                          className={[
                            styles.navFlowItem,
                            !selectionMode && isScrollActive ? styles.navFlowItemActive : '',
                            selectionMode ? styles.navFlowItemSelectionMode : '',
                            selectionMode && isLibrarySelected ? styles.navFlowItemLibrarySelected : '',
                            selectionMode && !isLibrarySelected && isScrollActive
                              ? styles.navFlowItemScrollActive
                              : '',
                          ].join(' ')}
                          onClick={() => onFlowSelect(flow.uuid)}
                        >
                          {selectionMode && (
                            <span
                              className={[
                                styles.flowCheckbox,
                                isLibrarySelected ? styles.flowCheckboxChecked : '',
                              ].join(' ')}
                            />
                          )}
                          <span className={styles.navFlowName}>
                            {flow.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.navFade} />
      </div>

      {!selectionMode && onAddFlow && (
        <div className={styles.navButtons}>
          <button className={styles.addFlowBtn} onClick={onAddFlow}>
            <img src={plusOutlineGrayIcon} alt="" width={16} height={16} />
            Add new flow
          </button>
        </div>
      )}
    </nav>
  );
};

CategoryNav.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      flows: PropTypes.array.isRequired,
    })
  ).isRequired,
  selectedFlowUuid: PropTypes.string,
  expandedCategories: PropTypes.instanceOf(Set).isRequired,
  onFlowSelect: PropTypes.func.isRequired,
  onCategoryToggle: PropTypes.func.isRequired,
  onAddFlow: PropTypes.func,
  selectionMode: PropTypes.bool,
  selectedLibraryFlowUuid: PropTypes.string,
  screenCount: PropTypes.number,
};

CategoryNav.defaultProps = {
  selectionMode: false,
  selectedLibraryFlowUuid: null,
  onAddFlow: null,
  screenCount: null,
};

export default CategoryNav;
